import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { getPurohitProfile } from "../../services/api";
import PurohitRegistration from "./PurohitRegistrationPage";
import PurohitEdit from "./PurohitEditPage";
import PurohitSubscription from "./PurohitSubscriptionPage";
import PurohitRenewSubscription from "./PurohitRenewSubscriptionPage";
import PaymentGateway from "../PaymentGateway";

import {
  Crown,
  UserPlus,
  Calendar,
  RefreshCcw,
  ChevronRight,
  FileText,
  CheckCircle2,
  Edit3,
} from "lucide-react";

export default function PurohitDashBoardPage() {
  const { auth, getCurrentUser, updatePurohitProfile } = useAuth();

  const [openRegistration, setOpenRegistration] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [openSubscription, setOpenSubscription] = useState(false);
  const [openRenewModal, setOpenRenewModal] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("1499");
  const [userName, setUserName] = useState("Purohit");
  const [userEmail, setUserEmail] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [hasProfile, setHasProfile] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check profile status on mount and when auth changes
  useEffect(() => {
    // PurohitDashBoardPage.jsx

    const checkProfileStatus = async () => {
      try {
        setLoading(true);

        // Get current user
        const currentUser = getCurrentUser();
        if (currentUser) {
          setUserName(currentUser.fullName || "Purohit");
          setUserEmail(currentUser.email || "");
          setUserPhone(currentUser.phone || "");
        }

        // Fetch latest profile data from API
        const response = await getPurohitProfile();
        const profileData = response.data?.profile || response.data;

        console.log("=== PROFILE STATUS CHECK ===");
        console.log("Profile data:", profileData);
        console.log("isComplete:", profileData?.isComplete);
        console.log("=== END ===");

        // Update AuthContext with latest profile
        if (profileData) {
          updatePurohitProfile(profileData);

          // ✅ CRITICAL: Set hasProfile based on isComplete flag
          const isProfileComplete = profileData.isComplete === true;
          setHasProfile(isProfileComplete);

          console.log("Profile complete status:", isProfileComplete);
        } else {
          setHasProfile(false);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);

        // ✅ If 404 or profile not found, definitely no complete profile
        if (error.response?.status === 404) {
          setHasProfile(false);
        }
      } finally {
        setLoading(false);
      }
    };
    // Only check if user is authenticated as purohit
    if (auth.isAuthenticated && auth.role === "purohit") {
      checkProfileStatus();
    }

    // Check if returning from registration/edit page
    const page = sessionStorage.getItem("editPageState");
    if (page === "registration") setOpenRegistration(true);
    if (page === "editing") setIsEditing(true);
  }, [auth.isAuthenticated, auth.role]);

  const lockScroll = () => (document.body.style.overflow = "hidden");
  const unlockScroll = () => (document.body.style.overflow = "unset");

  const openSubscriptionModal = () => {
    lockScroll();
    setOpenSubscription(true);
  };

  const closeSubscriptionModal = () => {
    unlockScroll();
    setOpenSubscription(false);
  };

  const openRenewSubscriptionModal = () => {
    lockScroll();
    setOpenRenewModal(true);
  };

  const closeRenewModal = () => {
    unlockScroll();
    setOpenRenewModal(false);
  };

  const handleUpgradeNow = () => {
    closeSubscriptionModal();
    setPaymentAmount("1499");
    setShowPayment(true);
    lockScroll();
  };

  const handleRenewNow = () => {
    closeRenewModal();
    setPaymentAmount("499");
    setShowPayment(true);
    lockScroll();
  };

  const handlePaymentClose = () => {
    unlockScroll();
    setShowPayment(false);
  };

  const handlePaymentSuccess = async () => {
    handlePaymentClose();

    // Refresh profile status from API
    try {
      const response = await getPurohitProfile();
      const profileData = response.data?.profile || response.data;

      if (profileData) {
        updatePurohitProfile(profileData);
        setHasProfile(profileData.isComplete === true);
      }
    } catch (error) {
      console.error("Error refreshing profile:", error);
    }
  };

  const closeForms = async () => {
    sessionStorage.removeItem("editPageState");
    sessionStorage.removeItem("registrationStep");
    sessionStorage.removeItem("purohitData");
    unlockScroll();
    setOpenRegistration(false);
    setIsEditing(false);

    // Refresh profile status from API
    try {
      const response = await getPurohitProfile();
      const profileData = response.data?.profile || response.data;

      console.log("Profile refreshed after close:", profileData);

      if (profileData) {
        updatePurohitProfile(profileData);
        setHasProfile(profileData.isComplete === true);
      }
    } catch (error) {
      console.error("Error refreshing profile:", error);
    }
  };

  const handleCreateProfile = () => {
    if (hasProfile) {
      alert(
        "You already have a profile! You can only create one profile per account. Use 'Edit Profile' to make changes."
      );
      return;
    }
    setOpenRegistration(true);
  };

  const handleEditProfile = () => {
    if (!hasProfile) {
      alert("Please create your profile first before editing.");
      return;
    }
    setIsEditing(true);
  };

  // If editing, show edit page
  if (isEditing) {
    return <PurohitEdit onClose={closeForms} />;
  }

  // If registration, show registration page
  if (openRegistration) {
    return <PurohitRegistration onClose={closeForms} />;
  }

  // If payment modal for subscription/renewal, show payment gateway
  if (showPayment) {
    return (
      <PaymentGateway
        amount={paymentAmount}
        userName={userName}
        userEmail={userEmail}
        userPhone={userPhone}
        onClose={handlePaymentClose}
        onSuccess={handlePaymentSuccess}
      />
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF7EA] pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-orange-600 font-semibold">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Dashboard Content */}
      <div className="min-h-screen bg-[#FFF7EA] pt-20">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 via-orange-500 to-orange-400 py-8 text-white shadow-lg">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center gap-3">
            <Crown size={36} className="sm:w-10 sm:h-10" />
            <div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold">
                Welcome, {userName}
              </h2>
              <p className="text-xs sm:text-sm opacity-90">Purohit Dashboard</p>
            </div>
          </div>
        </div>

        {/* Main Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-8 sm:space-y-10">
          {/* Profile Section - Conditional Rendering */}
          {!hasProfile ? (
            <div className="bg-white border-2 border-orange-400 rounded-2xl p-5 sm:p-6 shadow-md w-full">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-orange-100 flex items-center justify-center">
                    <UserPlus className="w-6 h-6 sm:w-7 sm:h-7 text-orange-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl sm:text-2xl font-bold text-orange-900">
                    Complete Your Profile
                  </h3>
                  <p className="text-orange-800 mt-2 sm:mt-3 text-sm sm:text-base">
                    Start accepting bookings after completing your profile. This
                    is a one-time setup.
                  </p>
                  <button
                    onClick={handleCreateProfile}
                    className="flex items-center gap-2 mt-4 sm:mt-5 px-5 sm:px-6 py-2 sm:py-2.5 text-sm rounded-lg text-white bg-gradient-to-r from-orange-600 to-red-600 shadow-md hover:brightness-110 transition-all"
                  >
                    <UserPlus size={18} /> Create Profile
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-2xl p-5 sm:p-6 shadow-md w-full">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 sm:w-7 sm:h-7 text-green-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl sm:text-2xl font-bold text-green-900">
                    Profile Complete! 🎉
                  </h3>
                  <p className="text-green-800 mt-2 sm:mt-3 text-sm sm:text-base">
                    Your profile is live and visible to devotees. You can update
                    your details anytime.
                  </p>
                  <div className="flex flex-wrap gap-3 mt-4 sm:mt-5">
                    {/* <button
                      onClick={handleEditProfile}
                      className="flex items-center gap-2 px-5 sm:px-6 py-2 sm:py-2.5 text-sm rounded-lg text-white bg-gradient-to-r from-green-600 to-emerald-600 shadow-md hover:brightness-110 transition-all"
                    >
                      <Edit3 size={16} /> Edit Profile
                    </button> */}
                    <button
                      onClick={() =>
                        window.open("/pandith-parichaya", "_blank")
                      }
                      className="flex items-center gap-2 px-5 sm:px-6 py-2 sm:py-2.5 text-sm rounded-lg text-green-700 bg-white border-2 border-green-300 shadow-sm hover:bg-green-50 transition-all"
                    >
                      View Public Profile
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Feature Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <Card
              title="Total Connects"
              color="blue"
              icon={
                <Calendar className="w-8 h-8 sm:w-10 sm:h-10 opacity-95 ml-auto" />
              }
              onClick={() => {}}
            />

            <Card
              title="Update Profile"
              color="orange"
              icon={
                <UserPlus className="w-8 h-8 sm:w-10 sm:h-10 opacity-95 ml-auto" />
              }
              onClick={handleEditProfile}
              disabled={!hasProfile}
            />

            <Card
              title="Subscription Plan"
              color="purple"
              icon={
                <Crown className="w-8 h-8 sm:w-10 sm:h-10 opacity-95 ml-auto" />
              }
              onClick={openSubscriptionModal}
            />

            <Card
              title="Renew Subscription"
              color="green"
              icon={
                <RefreshCcw className="w-8 h-8 sm:w-10 sm:h-10 opacity-95 ml-auto" />
              }
              onClick={openRenewSubscriptionModal}
            />
          </div>

          {/* Help Section */}
          <div className="bg-[#E7F1FF] border-2 border-blue-200 rounded-2xl shadow-md p-5 sm:p-6 w-full">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="text-blue-600 w-5 h-5 sm:w-6 sm:h-6" />
              <h3 className="text-lg sm:text-xl font-semibold text-blue-800">
                Need Help Getting Started?
              </h3>
            </div>
            <p className="text-gray-700 text-sm sm:text-base">
              Check tips to make your profile attractive and stand out to
              devotees.
            </p>
            <button className="mt-3 px-4 py-2 border-2 rounded-lg text-sm sm:text-base text-blue-600 border-blue-400 hover:bg-blue-100 transition-colors flex items-center gap-2">
              <FileText className="w-4 h-4" /> Help Center
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {openSubscription && (
        <PurohitSubscription
          onClose={closeSubscriptionModal}
          onUpgrade={handleUpgradeNow}
        />
      )}

      {openRenewModal && (
        <PurohitRenewSubscription
          onClose={closeRenewModal}
          onRenew={handleRenewNow}
        />
      )}
    </>
  );
}

function Card({ title, color, icon, onClick, disabled = false }) {
  const bg = {
    blue: "bg-blue-600 text-white",
    orange: "bg-orange-500 text-white",
    purple: "bg-purple-600 text-white",
    green: "bg-green-600 text-white",
  }[color];

  const text = {
    blue: "text-blue-700",
    orange: "text-orange-600",
    purple: "text-purple-600",
    green: "text-green-600",
  }[color];

  const buttonText = {
    "Total Bookings": "View Connects",
    "Update Profile": "Edit Now",
    "Subscription Plan": "View Benefits",
    "Renew Subscription": "Renew Now",
  }[title];

  return (
    <div
      className={`rounded-2xl shadow-md overflow-hidden bg-white border border-gray-200 hover:shadow-lg transition-shadow ${
        disabled ? "opacity-60" : ""
      }`}
    >
      <div
        className={`${bg} p-4 sm:p-5 h-28 sm:h-32 flex flex-col justify-between`}
      >
        <h3 className="text-base sm:text-lg font-semibold">{title}</h3>
        {icon}
      </div>
      <button
        onClick={onClick}
        disabled={disabled}
        className={`flex items-center justify-between w-full px-4 sm:px-5 py-3 font-semibold hover:bg-gray-50 transition-colors ${text} text-sm sm:text-base ${
          disabled ? "cursor-not-allowed" : "cursor-pointer"
        }`}
      >
        <span>{buttonText}</span>
        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
      </button>
    </div>
  );
}
